<template>
    <template v-if="!details">
        <IonHeader
            class="ion-no-border"
            data-cy="filters-modal-header"
        >
            <div class="w-full font-medium text-md border-b border-b-transparent transition-colors duration-300 py-2">
                <div class="w-full flex items-center justify-between px-3 overflow-hidden">
                    <DButton
                        theme="transparent"
                        class="mr-3 -ml-1 w-[38px] h-[38px] flex items-center justify-center rounded-full"
                        @click="cancel"
                    >
                        <PhIcon
                            icon="x"
                            weight="light"
                            :size="24"
                        />
                    </DButton>

                    <span
                        class="flex-1 truncate text-gray-900 font-medium"
                        :class="isIos ? 'text-center ml-[10px]' : 'text-left'"
                    >
                        {{ title }}
                    </span>

                    <div class="w-[60px] shrink flex justify-end">
                        <DButton
                            data-cy="filters-modal-clear-all"
                            theme="text-link"
                            :link-underline="false"
                            :disabled="!canClearAll"
                            @click="clearAll"
                        >
                            Clear all
                        </DButton>
                    </div>
                </div>
            </div>
        </IonHeader>
        <IonContent data-cy="filters-modal-content">
            <DForm class="pt-4">
                <slot />
                <div v-if="filterGroups.length">
                    <template
                        v-for="(filterGroup, index) in filterGroups"
                        :key="index"
                    >
                        <div
                            v-if="showDividers"
                            class="w-full h-2 surface-secondary"
                        />
                        <div
                            v-if="filterGroup.title"
                            class="px-4 pt-5 pb-2 content-sm-ux-semibold"
                        >
                            {{ filterGroup.title }}
                        </div>
                        <template
                            v-for="filter in filterGroup.filters"
                            :key="`${filter.dependency_id}-${filter.id}`"
                        >
                            <component
                                :is="widgetFilterComponents[filter.type]"
                                v-if="widgetFilterComponents[filter.type]"
                                :filter="filter"
                                :model-value="currentSelectedFilters[filter.dependency_id]?.[filter.id]"
                                @update:model-value="updateFilterValue(filter, $event)"
                                @open-details="openDetails"
                            />
                        </template>
                    </template>
                </div>
            </DForm>
        </IonContent>
    </template>
    <component
        :is="details.component"
        v-else
        v-bind="details.props"
        :filter="details.filter"
        :model-value="currentSelectedFilters[details.filter.dependency_id]?.[details.filter.id]"
        @update:model-value="updateFilterValue(details.filter, $event)"
        @close="closeDetails"
    />
    <IonFooter class="ion-no-border border-t border-neutral-base border-solid">
        <div class="flex w-full justify-stretch p-3">
            <DButton
                data-cy="filters-modal-confirm"
                wide
                class="mx-2"
                @click="confirm"
            >
                Apply
            </DButton>
        </div>
    </IonFooter>
</template>
<script setup>
    import {
        DButton, DForm,
    } from '@digistorm/spark'
    import { modalController } from '@ionic/vue'
    import {
        cloneDeep, isEmpty, isNil,
    } from 'lodash'
    import { useWidgetFilterHelpers } from '@/composables'
    import widgetFilterComponents from '@/constants/widgetFilterComponents'

    const props = defineProps({
        title: String,
        filterGroups: Array,
        selectedFilters: Object,
        applyChanges: Function,
        showDividers: {
            type: Boolean,
            default: true,
        },
    })

    const widgetFilterHelpers = useWidgetFilterHelpers()
    const deviceStore = useDeviceStore()

    const { isIos } = storeToRefs(deviceStore)

    const details = ref(null)

    // TODO: Build and only apply valid filters
    const currentSelectedFilters = ref(cloneDeep(props.selectedFilters ?? {}))

    const filterCount = computed(() => {
        return widgetFilterHelpers.activeCount(props.filterGroups, currentSelectedFilters.value)
    })

    const canClearAll = computed(() => filterCount.value > 0)

    const updateFilterValue = (filter, value) => {
        const dependencyFilters = currentSelectedFilters.value[filter.dependency_id]

        if (isNil(value)) {
            if (dependencyFilters) {
                delete dependencyFilters[filter.id]
                if (isEmpty(dependencyFilters)) {
                    delete currentSelectedFilters.value[filter.dependency_id]
                }
            }
            return
        }

        if (!dependencyFilters) {
            currentSelectedFilters.value[filter.dependency_id] = {}
        }
        currentSelectedFilters.value[filter.dependency_id][filter.id] = value
    }

    const clearAll = () => {
        currentSelectedFilters.value = {}
    }

    const cancel = () => {
        modalController.dismiss(null, 'cancel')
    }

    const confirm = () => {
        props.applyChanges(
            filterCount.value > 0
                ? currentSelectedFilters.value
                : {},
        )
        modalController.dismiss(null, 'confirm')
    }

    const openDetails = (value) => {
        details.value = value
    }

    const closeDetails = () => {
        details.value = null
    }
</script>
